import React, { useCallback } from 'react'

import {
  Button, Divider, InfoOutlined, classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, node, number, oneOf, string } from 'prop-types'

import TierMark from 'Pods/Profile/Settings/SubscriptionManagement/Shared/TierMark/TierMark'
import I18n from 'Pods/Shared/I18n'
import highlightTranslation from 'Pods/Shared/Utils/highlightTranslation'

import { formatLongDate } from 'Utils/FormatDate'
import formatPrice from 'Utils/formatPrice'
import { NOOP } from 'Utils/stableDefaults'

import BillingInfoItem from './BillingInfoItem'
import PlanNotice from './PlanNotice'
import StateNotice from './StateNotice'
import {
  PLAN_CHANGE_KIND, PLAN_TYPE, SUBSCRIPTION_STATUS, TIER, isPaymentFailedStatus,
} from '../../../constants'
import { scheduledChangeShape } from '../../../propTypes'

const EMPHASIS_CLASS = 'cgt-body-md-semibold plan-card__emphasis'
const PLAN_CHANGE_KEY = 'subscription_management.plan_change'

const BADGES = {
  [SUBSCRIPTION_STATUS.ACTIVE]: { key: 'subscription_management.active.badge_active', tone: 'success' },
  [SUBSCRIPTION_STATUS.PAUSE_SCHEDULED]: { key: 'subscription_management.paused.badge_scheduled', tone: 'neutral' },
  [SUBSCRIPTION_STATUS.PAUSED]: { key: 'subscription_management.paused.badge_paused', tone: 'neutral' },
  [SUBSCRIPTION_STATUS.PAST_DUE]: { key: 'subscription_management.payment_failed.badge', tone: 'error' },
  [SUBSCRIPTION_STATUS.REDEMPTION]: { key: 'subscription_management.payment_failed.badge', tone: 'error' },
  [SUBSCRIPTION_STATUS.PENDING_CANCEL]: { key: 'subscription_management.active.badge_cancelled', tone: 'error' },
  [SUBSCRIPTION_STATUS.SUSPENDED]: { key: 'subscription_management.state_notices.suspended.badge', tone: 'error' },
  [SUBSCRIPTION_STATUS.FULLY_CANCELED]: { key: 'subscription_management.active.badge_cancelled', tone: 'neutral' },
  [SUBSCRIPTION_STATUS.EXPIRED]: { key: 'subscription_management.active.badge_cancelled', tone: 'neutral' },
}

function CurrentPlanCard({
  tier,
  displayStatus,
  paymentMethod,
  billingTerm = PLAN_TYPE.MONTHLY,
  termPrice = null,
  nextBillingDate = null,
  pausedUntil = null,
  pauseScheduledAt = null,
  pauseResumesAt = null,
  accessEndsAt = null,
  endedOn = null,
  suspendedAt = null,
  updatePaymentUrl,
  updatePaymentAction = null,
  canCancel = false,
  isCancelLoading = false,
  onCancel: handleCancel = NOOP,
  canResume = false,
  isResumeLoading = false,
  onResume: handleResume = NOOP,
  canCancelScheduledPause = false,
  isCancelScheduledPauseLoading = false,
  resumeInProgress = false,
  canRetry = false,
  isRetryLoading = false,
  isRetrySubmitted = false,
  onRetry: handleRetry = NOOP,
  canceledByAdmin = false,
  canRestore = false,
  isRestoreLoading = false,
  onRenew: handleRenew = NOOP,
  onReactivate: handleReactivate = NOOP,
  scheduledChange = null,
  canSwitchPlan = false,
  onSwitchPlan: handleSwitchPlan = NOOP,
  onKeepPlan: handleKeepPlan = NOOP,
  isKeepPlanLoading = false,
  locale = 'en',
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  const isAnnual = billingTerm === PLAN_TYPE.ANNUAL
  const isActive = displayStatus === SUBSCRIPTION_STATUS.ACTIVE
  const isPendingCancel = displayStatus === SUBSCRIPTION_STATUS.PENDING_CANCEL
  const isEnded = [SUBSCRIPTION_STATUS.FULLY_CANCELED, SUBSCRIPTION_STATUS.EXPIRED].includes(displayStatus)
  const badge = BADGES[displayStatus] || BADGES[SUBSCRIPTION_STATUS.ACTIVE]
  // Payment failed and suspended carry the only recovery action, so a pending plan change never hides them.
  const showStateNotice = !scheduledChange
    || isPaymentFailedStatus(displayStatus)
    || displayStatus === SUBSCRIPTION_STATUS.SUSPENDED

  const termPriceLabel = translate(`subscription_management.active.price_per_${isAnnual ? 'year' : 'month'}`, {
    price: formatPrice(termPrice),
  })
  const pausedUntilDate = formatLongDate(pausedUntil, locale, 'UTC')
  const nextBillingDateLabel = formatLongDate(nextBillingDate, locale, 'UTC')
  const pauseScheduledDate = formatLongDate(pauseScheduledAt, locale, 'UTC')
  let nextBillingValue = nextBillingDateLabel || translate('subscription_management.active.billing.no_renewal')
  if (displayStatus === SUBSCRIPTION_STATUS.SUSPENDED) {
    nextBillingValue = translate('subscription_management.active.billing.billing_suspended')
  } else if (displayStatus === SUBSCRIPTION_STATUS.PAUSE_SCHEDULED) {
    nextBillingValue = translate('subscription_management.active.billing.pauses_on', { date: pauseScheduledDate })
  }
  const planLabel = translate(`subscription_management.active.${isAnnual ? 'annual' : 'monthly'}_plan`)
  let bannerMessageKey = `subscription_management.active.renews_${isAnnual ? 'annual' : 'monthly'}`

  const tierName = (planTier) => translate(`${PLAN_CHANGE_KEY}.tiers.${planTier}`)
  const isScheduledDowngrade = scheduledChange?.kind === PLAN_CHANGE_KIND.DOWNGRADE
  const scheduledDate = scheduledChange && formatLongDate(scheduledChange.effectiveAt, locale, 'UTC')
  const scheduledPrice = scheduledChange && translate(
    `subscription_management.active.price_per_${scheduledChange.billingTerm === PLAN_TYPE.ANNUAL ? 'year' : 'month'}`,
    { price: formatPrice(scheduledChange.price) },
  )
  if (scheduledChange) {
    bannerMessageKey = `${PLAN_CHANGE_KEY}.pending.banner_${isScheduledDowngrade ? 'downgrade' : 'to_monthly'}`
  }
  const bannerDate = scheduledDate || nextBillingDateLabel
  const switchPlanLink = (
    <Button
      type="ghost"
      size="small"
      actionType="button"
      customClass="cgt-body-md-semibold plan-card__switch-link"
      onClick={handleSwitchPlan}
      disabled={!canSwitchPlan}
    >
      {translate('subscription_management.active.switch_plan')}
    </Button>
  )
  let headerAction = null
  if (canCancel) {
    headerAction = (
      <Button
        type="ghost"
        size="small"
        customClass="cgt-body-md-semibold plan-card__cancel-link"
        onClick={handleCancel}
        disabled={isCancelLoading}
      >
        {translate('subscription_management.active.cancel_button')}
      </Button>
    )
  } else if (canRestore && (isPendingCancel || isEnded)) {
    headerAction = (
      <div className="plan-card__header-actions">
        {/* An upgrade also lifts the scheduled cancellation, so the BE keeps it on offer while pending cancel. */}
        {isPendingCancel && canSwitchPlan && switchPlanLink}
        <Button
          type="ghost"
          size="small"
          actionType="button"
          customClass="cgt-body-md-semibold plan-card__restore-link"
          onClick={isPendingCancel ? handleRenew : handleReactivate}
          disabled={isRestoreLoading}
        >
          {translate(`subscription_management.active.${isPendingCancel ? 'renew' : 'reactivate'}_button`)}
        </Button>
      </div>
    )
  }

  return (
    <div className="plan-card plan-card--restructure">
      {isActive && (
        <div className="plan-card__banner">
          <InfoOutlined width="18" height="18" className="plan-card__banner-icon" aria-hidden="true" />
          <p className="cgt-body-md plan-card__banner-text">
            {bannerDate
              ? highlightTranslation(translate, bannerMessageKey, { plan: planLabel, date: bannerDate },
                EMPHASIS_CLASS, scheduledChange ? { target: tierName(scheduledChange.tier) } : undefined)
              : <span className={EMPHASIS_CLASS}>{planLabel}</span>}
          </p>
          {!scheduledChange && switchPlanLink}
        </div>
      )}

      <div className="plan-card__body">
        <div className="plan-card__header">
          <div className="plan-card__title-group">
            <TierMark tier={tier} size={24} />
            <h3 className="cgt-heading-300 plan-card__name">
              {translate(`subscription_management.active.plan_name.${tier}`)}
            </h3>
            <span className={classnames('cgt-caption-semibold', 'plan-card__badge', `plan-card__badge--${badge.tone}`)}>
              {translate(badge.key)}
            </span>
          </div>
          {headerAction}
        </div>

        <Divider customClass="plan-card__divider plan-card__divider--restructure" />

        <div className="plan-card__billing-grid plan-card__billing-grid--restructure">
          <BillingInfoItem
            variant="restructure"
            iconType="billingCycle"
            label={translate('subscription_management.active.billing.billing_cycle')}
            value={translate(`subscription_management.billing_cycle.${isAnnual ? 'yearly' : 'monthly'}`)}
          />
          <BillingInfoItem
            variant="restructure"
            iconType="renewsOn"
            label={translate('subscription_management.active.billing.next_billing_date')}
            value={nextBillingValue}
          />
          <BillingInfoItem
            variant="restructure"
            iconType="price"
            label={translate('subscription_management.active.billing.price')}
            value={termPriceLabel}
          />
          <BillingInfoItem
            variant="restructure"
            iconType="payment"
            label={translate('subscription_management.active.billing.payment_method')}
            value={paymentMethod}
            action={updatePaymentAction}
          />
        </div>

        {scheduledChange && (
          <PlanNotice
            variant="scheduled"
            title={isScheduledDowngrade
              ? translate(`${PLAN_CHANGE_KEY}.pending.title_downgrade`, {
                plan: tierName(scheduledChange.tier), date: scheduledDate,
              })
              : translate(`${PLAN_CHANGE_KEY}.pending.title_to_monthly`, { date: scheduledDate })}
            description={isScheduledDowngrade
              ? translate(`${PLAN_CHANGE_KEY}.pending.description_downgrade`, {
                currentPlan: tierName(tier),
                plan: translate(`${PLAN_CHANGE_KEY}.pending.plan_with_term.${scheduledChange.billingTerm}`, {
                  plan: tierName(scheduledChange.tier),
                }),
                price: scheduledPrice,
              })
              : translate(`${PLAN_CHANGE_KEY}.pending.description_to_monthly`, {
                plan: tierName(tier), price: scheduledPrice,
              })}
            action={(
              <Button
                type="secondary"
                size="small"
                actionType="button"
                customClass="plan-notice__action"
                onClick={handleKeepPlan}
                disabled={isKeepPlanLoading}
              >
                {isScheduledDowngrade
                  ? translate(`${PLAN_CHANGE_KEY}.keep_plan`, { plan: tierName(tier) })
                  : translate(`${PLAN_CHANGE_KEY}.pending.keep_current_plan`)}
              </Button>
            )}
          />
        )}

        {showStateNotice && (
          <StateNotice
            displayStatus={displayStatus}
            translate={translate}
            pausedUntilDate={pausedUntilDate}
            pauseScheduledDate={pauseScheduledDate}
            pauseResumesDate={formatLongDate(pauseResumesAt, locale, 'UTC')}
            accessEndsDate={formatLongDate(accessEndsAt, locale, 'UTC')}
            endedOn={endedOn}
            suspendedDate={formatLongDate(suspendedAt, locale, 'UTC')}
            termPriceLabel={termPriceLabel}
            updatePaymentUrl={updatePaymentUrl}
            resumeInProgress={resumeInProgress}
            isRetrySubmitted={isRetrySubmitted}
            canResume={canResume}
            isResumeLoading={isResumeLoading}
            onResume={handleResume}
            canCancelScheduledPause={canCancelScheduledPause}
            isCancelScheduledPauseLoading={isCancelScheduledPauseLoading}
            canRetry={canRetry}
            isRetryLoading={isRetryLoading}
            onRetry={handleRetry}
            canceledByAdmin={canceledByAdmin}
            canRestore={canRestore}
            isRestoreLoading={isRestoreLoading}
            onRenew={handleRenew}
            onReactivate={handleReactivate}
          />
        )}
      </div>
    </div>
  )
}

CurrentPlanCard.propTypes = {
  tier: oneOf([TIER.ESSENTIALS, TIER.PREMIUM]).isRequired,
  displayStatus: string.isRequired,
  paymentMethod: string.isRequired,
  billingTerm: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.ANNUAL]),
  termPrice: number,
  nextBillingDate: string,
  pausedUntil: string,
  pauseScheduledAt: string,
  pauseResumesAt: string,
  accessEndsAt: string,
  endedOn: string,
  suspendedAt: string,
  updatePaymentUrl: string.isRequired,
  updatePaymentAction: node,
  canCancel: bool,
  isCancelLoading: bool,
  onCancel: func,
  canResume: bool,
  isResumeLoading: bool,
  onResume: func,
  canCancelScheduledPause: bool,
  isCancelScheduledPauseLoading: bool,
  resumeInProgress: bool,
  canRetry: bool,
  isRetryLoading: bool,
  isRetrySubmitted: bool,
  onRetry: func,
  canceledByAdmin: bool,
  canRestore: bool,
  isRestoreLoading: bool,
  onRenew: func,
  onReactivate: func,
  scheduledChange: scheduledChangeShape,
  canSwitchPlan: bool,
  onSwitchPlan: func,
  onKeepPlan: func,
  isKeepPlanLoading: bool,
  locale: string,
}

export default CurrentPlanCard
